import { auth } from "../lib/firebase";
import { matchKnowledgeQuery } from "./assistantKnowledge";

export interface AssistantChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  suggestedQuestions?: string[];
  suggestedLinks?: Array<{ label: string; path: string }>;
}

export interface AssistantChatResult {
  reply: string;
  suggestedQuestions?: string[];
  suggestedLinks?: Array<{ label: string; path: string }>;
  isOfflineFallback?: boolean;
}

const FUNCTIONS_URL =
  import.meta.env.VITE_FIREBASE_FUNCTIONS_URL ||
  "https://phq-assistantchat-qbuhtyitjq-uc.a.run.app";

/**
 * Sends a user message to the Proposal HQ AI Assistant.
 * Connects to the phq_assistantChat Cloud Function with fallback to the local grounded knowledge engine.
 */
export async function sendAssistantMessage(
  message: string,
  history: AssistantChatMessage[] = [],
): Promise<AssistantChatResult> {
  const trimmed = message.trim();
  if (!trimmed) {
    throw new Error("Message cannot be empty.");
  }

  // Attempt to call Cloud Function backend first
  try {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;

    if (token) {
      const response = await fetch(FUNCTIONS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: trimmed,
          history: history.map((h) => ({ role: h.role, content: h.content })),
        }),
      });

      if (response.ok) {
        const data = (await response.json()) as {
          success?: boolean;
          reply?: string;
          suggestedQuestions?: string[];
          suggestedLinks?: Array<{ label: string; path: string }>;
        };

        if (data.reply) {
          return {
            reply: data.reply,
            suggestedQuestions: data.suggestedQuestions,
            suggestedLinks: data.suggestedLinks,
            isOfflineFallback: false,
          };
        }
      }
    }
  } catch (err) {
    console.warn("phq_assistantChat network call failed; falling back to knowledge base:", err);
  }

  // Fallback to grounded local knowledge base engine
  const localMatch = matchKnowledgeQuery(trimmed);
  return {
    reply: localMatch.reply,
    suggestedQuestions: localMatch.suggestedQuestions,
    suggestedLinks: localMatch.suggestedLinks,
    isOfflineFallback: true,
  };
}
